<template>
  <RevTable :collection="integrations" :columns transparent-header>
    <template #body-name="{ item }">
      {{ item.integrator.name }}
    </template>
    <template #body-status="{ item }">
      <RevTag :label="item.status.label" variant="success" />
    </template>
    <template #body-date="{ item }">
      <span data-allow-mismatch>
        {{
          i18n.date(new Date(item.date), {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
          })
        }}
      </span>
    </template>

    <template #body-actions="{ item }">
      <div class="flex items-center justify-end">
        <RevButtonIcon
          :alternative-text="i18n(translations.delete)"
          :icon="IconTrash"
          variant="secondary"
          @click="handleDelete(item.id)"
        />
      </div>
    </template>
  </RevTable>

  <RevDialog :name="MODAL_NAMES.DELETE">
    <template #body>
      <h3 class="heading-3 text-center">
        {{ i18n(translations.deleteConfirmationMessage) }}
      </h3>
    </template>

    <template #footer="{ close }">
      <div class="flex justify-center space-x-16 pb-16 pt-32">
        <RevButton class="block" variant="secondary" @click="close">
          {{ i18n(translations.cancelDelete) }}
        </RevButton>
        <RevButton
          class="block"
          :loading="isDeleting"
          variant="primaryDestructive"
          @click="handleConfirmDelete(close)"
        >
          {{ i18n(translations.confirmDelete) }}
        </RevButton>
      </div>
    </template>
  </RevDialog>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevDialog } from '@ds/components/Dialog'
import { openModal } from '@ds/components/ModalBase'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { IconTrash } from '@ds/icons/IconTrash'

import { MODAL_NAMES } from '../../Integration.constants'

import translations from './IntegrationsList.translations'

const emit = defineEmits(['deleted'])

defineProps<{ integrations: merchantsAPI.Integration[] }>()

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const isDeleting = ref(false)
const integrationIdToDelete = ref<null | merchantsAPI.Integration['id']>(null)

const columns = [
  { key: 'name', label: i18n(translations.name) },
  { key: 'status', label: i18n(translations.status) },
  { key: 'date', label: i18n(translations.date) },
  { key: 'actions', label: '' },
]

async function handleConfirmDelete(close: () => void) {
  isDeleting.value = true
  try {
    await $httpFetch(merchantsAPI.deleteIntegration, {
      pathParams: { id: integrationIdToDelete.value },
    })
    emit('deleted')
  } catch (error) {
    openErrorToast()
  } finally {
    isDeleting.value = false
    close()
  }
}

function handleDelete(integrationId: merchantsAPI.Integration['id']) {
  integrationIdToDelete.value = integrationId
  openModal(MODAL_NAMES.DELETE)
}
</script>
